import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Alert, Box, Button, Chip, Collapse, IconButton, Skeleton, Tooltip, Typography } from "@mui/material";
import { MatchProposalAssistantExpertReport, MatchProposalAssistantReport } from "@src/api/match";
import { useMemo, useState } from "react";

interface ProposalAssistantReportProps {
	report: MatchProposalAssistantReport | null;
	isLoading: boolean;
	isGenerating: boolean;
	isGeneratingOverall?: boolean;
	generationError: string | null;
	onGenerate: (forceRegenerate?: boolean) => void;
	onGenerateOverall?: () => void;
	hasDeadlineSet?: boolean;
	visibleExpertIds?: string[];
	showGenerateButton?: boolean;
}

const cleanText = (value?: string | null) =>
	String(value || "")
		.replace(/\s+/g, " ")
		.trim();

const splitByDelimiters = (value?: string | null) => {
	const raw = String(value || "")
		.replace(/\r/g, "\n")
		.trim();
	if (!raw) return [];

	return raw
		.split(/\n|•/g)
		.map((line) => line.trim())
		.filter(Boolean)
		.map((line) => line.replace(/^[-•·]\s*/, "").trim());
};

const uniquePoints = (items: string[], max: number, maxLen: number) => {
	const seen = new Set<string>();
	const result: string[] = [];

	items.forEach((item) => {
		const normalized = cleanText(item).replace(/^[-•]\s*/, "");
		if (!normalized) return;
		if (seen.has(normalized)) return;
		seen.add(normalized);
		result.push(normalized.slice(0, maxLen));
	});

	return result.slice(0, max);
};

const fromTextToPoints = (value?: string | null, max = 3, maxLen = 180) =>
	uniquePoints(splitByDelimiters(value), max, maxLen);

const splitReasonLeadAndEffect = (point: string) => {
	const normalized = String(point || "").replace(/\r/g, "\n");
	const dashed = normalized.match(/^(.*?)\s+-\s+(.+)$/);
	if (dashed) {
		return {
			lead: cleanText(dashed[1]),
			tail: cleanText(dashed[2]),
		};
	}
	const lines = normalized
		.split(/\n+/g)
		.map((line) => cleanText(line))
		.filter(Boolean);
	if (lines.length >= 2) {
		return {
			lead: lines[0],
			tail: lines.slice(1).join(" "),
		};
	}
	return { lead: cleanText(normalized), tail: "" };
};

const SECTION_CONFIG = [
	{ keys: ["suitabilityReason", "interviewInsights"] as const, label: "프로필 & 인터뷰 인사이트", color: "#3b82f6", bgColor: "#eff6ff", maxPerKey: [5, 3] },
	{ keys: ["proposalHighlights"] as const, label: "제안서 핵심 포인트", color: "#8b5cf6", bgColor: "#f5f3ff", maxPerKey: [3] },
] as const;

const ensurePeriod = (text: string) => {
	const t = text.trim();
	if (!t) return t;
	return /[.!?。]$/.test(t) ? t : t + ".";
};

const ReasonBullet: React.FC<{ point: string; id: string }> = ({ point, id }) => {
	const { lead, tail } = splitReasonLeadAndEffect(point);
	return (
		<Typography
			key={id}
			sx={{ fontSize: 14, color: "#374151", lineHeight: 1.65, mb: 0.35 }}
		>
			•{" "}
			{tail ? (
				<Typography
					component="span"
					sx={{ fontWeight: 700, color: "#1f2937", fontSize: "inherit", lineHeight: "inherit" }}
				>
					{lead}
				</Typography>
			) : (
				<Typography component="span" sx={{ fontSize: "inherit", lineHeight: "inherit" }}>
					{ensurePeriod(lead)}
				</Typography>
			)}
			{tail ? (
				<Typography
					component="span"
					sx={{ display: "block", mt: 0.1, pl: 1.5, fontSize: "inherit", lineHeight: "inherit" }}
				>
					{ensurePeriod(tail)}
				</Typography>
			) : null}
		</Typography>
	);
};

export const SectionToggle: React.FC<{
	label: string;
	color: string;
	bgColor: string;
	points: string[];
	expertId: string;
}> = ({ label, color, bgColor, points, expertId }) => {
	const [open, setOpen] = useState(false);
	if (points.length === 0) return null;

	return (
		<Box sx={{
			border: `1.5px solid ${bgColor}`,
			borderRadius: "10px",
			overflow: "hidden",
			backgroundColor: open ? "#fff" : bgColor,
			transition: "background-color 200ms",
		}}>
			<Box
				onClick={() => setOpen((prev) => !prev)}
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					px: 1.25,
					py: 0.8,
					cursor: "pointer",
					userSelect: "none",
					"&:hover": { backgroundColor: "rgba(0,0,0,0.02)" },
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
					{!open && (
						<Typography sx={{
							fontSize: 10.5,
							color,
							backgroundColor: "rgba(255,255,255,0.7)",
							px: 0.7,
							py: 0.15,
							borderRadius: "3px",
							fontWeight: 600,
						}}>
							Click
						</Typography>
					)}
					<Typography sx={{ fontSize: 13, fontWeight: 700, color }}>
						{label}
					</Typography>
				</Box>
				{open
					? <ExpandLessRoundedIcon sx={{ fontSize: 18, color }} />
					: <ExpandMoreRoundedIcon sx={{ fontSize: 18, color }} />
				}
			</Box>
			<Collapse in={open} timeout={200}>
				<Box sx={{ px: 1.25, pb: 1.2, pt: 0.3 }}>
					{points.map((point, i) => (
						<ReasonBullet
							key={`${label}-${expertId}-${i}`}
							point={point}
							id={`${label}-${expertId}-${i}`}
						/>
					))}
				</Box>
			</Collapse>
		</Box>
	);
};

export const SummaryToggle: React.FC<{
	headline: string;
	decisionGuide?: string;
	expertId: string;
}> = ({ headline, decisionGuide, expertId }) => {
	const [open, setOpen] = useState(false);
	const color = "#059669";
	const bgColor = "#ecfdf5";

	return (
		<Box sx={{
			border: `1.5px solid ${bgColor}`,
			borderRadius: "10px",
			overflow: "hidden",
			backgroundColor: open ? "#fff" : bgColor,
			transition: "background-color 200ms",
		}}>
			<Box
				onClick={() => setOpen((prev) => !prev)}
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					px: 1.25,
					py: 0.8,
					cursor: "pointer",
					userSelect: "none",
					"&:hover": { backgroundColor: "rgba(0,0,0,0.02)" },
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
					{!open && (
						<Typography sx={{
							fontSize: 10.5,
							color,
							backgroundColor: "rgba(255,255,255,0.7)",
							px: 0.7,
							py: 0.15,
							borderRadius: "3px",
							fontWeight: 600,
						}}>
							Click
						</Typography>
					)}
					<Typography sx={{ fontSize: 13, fontWeight: 700, color }}>
						Summary
					</Typography>
				</Box>
				{open
					? <ExpandLessRoundedIcon sx={{ fontSize: 18, color }} />
					: <ExpandMoreRoundedIcon sx={{ fontSize: 18, color }} />
				}
			</Box>
			<Collapse in={open} timeout={200}>
				<Box sx={{ px: 1.25, pb: 1.2, pt: 0.3 }}>
					<Typography sx={{ fontSize: { xs: 13, md: 14 }, fontWeight: 700, color: "#111827", mb: 0.3 }}>
						{headline}
					</Typography>
					{decisionGuide ? (
						<Typography sx={{ fontSize: { xs: 12, md: 13 }, color: "#6b7280", lineHeight: 1.7, whiteSpace: "pre-line" }}>
							{decisionGuide.replace(/([.!?]) /g, "$1\n")}
						</Typography>
					) : null}
				</Box>
			</Collapse>
		</Box>
	);
};

const ExpertSection: React.FC<{
	expert: MatchProposalAssistantExpertReport;
	isLast: boolean;
	summary?: { headline: string; decisionGuide?: string };
}> = ({ expert, isLast, summary }) => {
	return (
		<Box
			sx={{
				borderBottom: isLast ? "none" : "1px solid #e5e7eb",
				px: 1.5,
				pt: 0.5,
				pb: 1,
			}}
		>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
				{summary?.headline ? (
					<SummaryToggle
						headline={summary.headline}
						decisionGuide={summary.decisionGuide}
						expertId={expert.matchedExpertDataUuid}
					/>
				) : null}
				{SECTION_CONFIG.map((section) => {
					const allPoints = section.keys.flatMap((key, ki) =>
						fromTextToPoints(expert[key], section.maxPerKey[ki], 260),
					);
					return (
						<SectionToggle
							key={section.label}
							label={section.label}
							color={section.color}
							bgColor={section.bgColor}
							points={allPoints}
							expertId={expert.matchedExpertDataUuid}
						/>
					);
				})}
			</Box>
		</Box>
	);
};

const ProposalAssistantReport: React.FC<ProposalAssistantReportProps> = ({
	report,
	isLoading,
	isGenerating,
	isGeneratingOverall = false,
	generationError,
	onGenerate,
	onGenerateOverall,
	hasDeadlineSet = false,
	visibleExpertIds,
	showGenerateButton = false,
}) => {
	const experts = useMemo(() => {
		const rows = report?.experts || [];
		if (!visibleExpertIds || visibleExpertIds.length === 0) return rows;
		const visibleSet = new Set(visibleExpertIds);
		return rows.filter((expert) => visibleSet.has(expert.matchedExpertDataUuid));
	}, [report?.experts, visibleExpertIds]);

	return (
		<Box sx={{ width: "100%" }}>
			{/* 헤더 */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 0.75,
					mb: 2,
				}}
			>
				<AutoAwesomeIcon sx={{ color: "#7c3aed", fontSize: { xs: 20, md: 22 } }} />
				<Typography
					sx={{
						fontSize: { xs: 18, md: 22 },
						fontWeight: 800,
						color: "#111827",
						lineHeight: 1.2,
						flex: 1,
					}}
				>
					후보자 비교 분석 AI
				</Typography>
				</Box>

			{generationError ? (
				<Alert severity="warning" sx={{ mb: 2 }}>
					{generationError}
				</Alert>
			) : null}

			{/* 로딩 */}
			{isLoading && !report ? (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
					<Skeleton variant="rounded" height={64} />
					<Skeleton variant="rounded" height={120} />
				</Box>
			) : null}

			{/* 리포트 없음 */}
			{!isLoading && !report ? (
				<Box
					sx={{
						border: "1px dashed #d4bffc",
						borderRadius: "12px",
						background:
							"linear-gradient(145deg, rgba(250,247,255,0.96) 0%, rgba(255,255,255,0.98) 100%)",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						textAlign: "center",
						py: 4,
						px: 3,
					}}
				>
					{showGenerateButton ? (
						<>
							<Typography sx={{ fontSize: { xs: 14, md: 16 }, color: "#374151", mb: 1.2 }}>
								AI가 이 전문가의 제안서를 분석해 드립니다.
							</Typography>
							<Button
								variant="contained"
								onClick={() => onGenerate(false)}
								disabled={isGenerating}
								sx={{
									borderRadius: "10px",
									fontWeight: 700,
									fontSize: { xs: 13, md: 15 },
									backgroundColor: "#7c3aed",
									"&:hover": { backgroundColor: "#6d28d9" },
								}}
							>
								{isGenerating ? "분석 중..." : "AI 분석 시작"}
							</Button>
						</>
					) : (
						<>
							<Typography sx={{ fontSize: { xs: 15, md: 17 }, fontWeight: 700, color: "#374151", mb: 1.5 }}>
								아직 생성된 분석 리포트가 없습니다
							</Typography>
							<Typography sx={{ fontSize: { xs: 13, md: 14 }, color: "#6b7280", lineHeight: 1.7 }}>
								각 전문가의 <b>제안 검토</b> 버튼을 눌러 업무 제안서를 확인하면,{"\n"}
								AI가 전문가를 종합적으로 분석합니다.{"\n"}
								분석이 완료되면 이곳에서 전문가별 비교 리포트를 확인할 수 있습니다.
							</Typography>
						</>
					)}
				</Box>
			) : null}

			{/* 리포트 렌더링 */}
			{report ? (
				<>
					{experts.length === 0 ? (
						<Box sx={{ p: 2 }}>
							<Alert severity="info">비교 가능한 전문가 데이터가 없습니다.</Alert>
						</Box>
					) : null}

					{/* AI 컨설턴트 총평 */}
					{experts.length > 0 && report.overallAssessment ? (
						<Box
							sx={{
								border: "1px solid #e5e7eb",
								borderRadius: "12px",
								backgroundColor: "#ffffff",
								mb: 2.5,
								overflow: "hidden",
							}}
						>
							<Box sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								px: 1.5,
								py: 0.8,
								backgroundColor: "#f8fafc",
								borderBottom: "1px solid #e5e7eb",
							}}>
								<Typography sx={{ fontSize: 14, fontWeight: 800, color: "#111827" }}>
									AI 컨설턴트 총평
								</Typography>
							</Box>
							<Box sx={{ px: 2, py: 1.5 }}>
								<Typography
									sx={{
										fontSize: { xs: 13.5, md: 14.5 },
										color: "#374151",
										lineHeight: 1.8,
										whiteSpace: "pre-line",
									}}
								>
									{report.overallAssessment}
								</Typography>
							</Box>
						</Box>
					) : null}

					{/* 총평 생성 버튼 (deadline 세팅됨 + 총평 아직 없음) */}
					{experts.length > 0 && !report.overallAssessment && hasDeadlineSet && onGenerateOverall ? (
						<Box sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}>
							<Button
								variant="outlined"
								onClick={() => onGenerateOverall()}
								disabled={isGeneratingOverall}
								sx={{
									borderRadius: "10px",
									fontWeight: 700,
									fontSize: { xs: 13, md: 14 },
									borderColor: "#7c3aed",
									color: "#7c3aed",
									"&:hover": { borderColor: "#6d28d9", backgroundColor: "rgba(124,58,237,0.04)" },
								}}
							>
								{isGeneratingOverall ? "총평 생성 중..." : "AI 컨설턴트 총평 생성하기"}
							</Button>
						</Box>
					) : null}

					{/* 비교 표 (컨설턴트 코멘트) */}
					{experts.length > 0 ? (
						<Box
							sx={{
								overflowX: "auto",
								border: "1px solid #e5e7eb",
								borderRadius: "12px",
								backgroundColor: "#ffffff",
								mb: 2.5,
							}}
						>
							<Box sx={{ minWidth: 680 }}>
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: "160px minmax(400px, 1fr)",
										borderBottom: "1px solid #e5e7eb",
										backgroundColor: "#f8fafc",
										alignItems: "center",
									}}
								>
									<Typography
										sx={{ p: 1.4, fontSize: 14, fontWeight: 800, color: "#111827", textAlign: "center" }}
									>
										전문가
									</Typography>
									<Typography
										sx={{ p: 1.4, fontSize: 14, fontWeight: 800, color: "#111827", borderLeft: "1px solid #e5e7eb", textAlign: "center" }}
									>
										AI 컨설턴트 코멘트
									</Typography>
								</Box>
								{experts.map((expert, index) => (
									<Box
										key={`table-${expert.matchedExpertDataUuid}`}
										sx={{
											display: "grid",
											gridTemplateColumns: "160px minmax(400px, 1fr)",
											borderBottom: index === experts.length - 1 ? "none" : "1px solid #e5e7eb",
											alignItems: "stretch",
										}}
									>
										<Box
											sx={{
												p: 1.4,
												display: "flex",
												flexDirection: "column",
												alignItems: "center",
												justifyContent: "center",
												textAlign: "center",
											}}
										>
											<Typography sx={{ fontSize: 16, fontWeight: 800, color: "#111827", lineHeight: 1.3 }}>
												{expert.expertName}
											</Typography>
											<Typography sx={{ mt: 0.3, fontSize: 13, color: "#6b7280" }}>
												{expert.expertJob || "직무 정보 없음"}
											</Typography>
										</Box>
										<Box
											sx={{
												p: 1.4,
												borderLeft: "1px solid #e5e7eb",
												display: "flex",
												alignItems: "center",
											}}
										>
											<Typography sx={{ fontSize: { xs: 13.5, md: 14.5 }, color: "#374151", lineHeight: 1.7 }}>
												{expert.consultantComment || ""}
											</Typography>
										</Box>
									</Box>
								))}
							</Box>
						</Box>
					) : null}

					{/* 전문가별 상세 (기존) */}
					<Box
						sx={{
							border: "1px solid #e5e7eb",
							borderRadius: "12px",
							backgroundColor: "#ffffff",
							overflow: "hidden",
						}}
					>
						{experts.map((expert, index) => (
							<Box key={expert.matchedExpertDataUuid} sx={{ borderBottom: index === experts.length - 1 ? "none" : "1px solid #e5e7eb" }}>
								{/* 전문가 이름 (브라우저 바) */}
								<Box sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									px: 1.5,
									py: 0.7,
									backgroundColor: "#f1f5f9",
									borderBottom: "1px solid #e2e8f0",
									borderTopLeftRadius: index === 0 ? "12px" : 0,
									borderTopRightRadius: index === 0 ? "12px" : 0,
								}}>
									<Typography sx={{ fontSize: { xs: 14, md: 15 }, fontWeight: 700, color: "#374151" }}>
										{expert.expertName}
									</Typography>
								</Box>

								{/* 섹션 콘텐츠 */}
								<ExpertSection
									expert={expert}
									isLast={true}
									summary={expert.expertHeadline ? { headline: expert.expertHeadline, decisionGuide: expert.expertDecisionGuide } : undefined}
								/>
							</Box>
						))}
					</Box>
				</>
			) : null}
		</Box>
	);
};

export default ProposalAssistantReport;
